/**
 * Rate Limiter Utility
 * Client-side rate limiting to prevent API abuse and reduce costs
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

const DEFAULT_CONFIGS: Record<string, RateLimitConfig> = {
  // Sync operations - max 1 per 5 seconds
  sync: { maxRequests: 1, windowMs: 5000 },

  // API calls - max 30 per minute
  api: { maxRequests: 30, windowMs: 60000 },

  // Login attempts - max 5 per 15 minutes
  login: { maxRequests: 5, windowMs: 900000 },

  // Document generation - max 10 per minute
  document: { maxRequests: 10, windowMs: 60000 },

  // Search/filter - max 20 per 10 seconds
  search: { maxRequests: 20, windowMs: 10000 },
};

class RateLimiter {
  private static instance: RateLimiter;
  private limits: Map<string, RateLimitEntry> = new Map();
  private configs: Record<string, RateLimitConfig>;

  private constructor() {
    this.configs = { ...DEFAULT_CONFIGS };
  }

  public static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  /**
   * Check if action is allowed and consume one request
   * @param category Rate limit category (sync, api, login, etc.)
   * @param key Optional unique key (e.g., user ID, IP)
   * @returns Object with allowed status and remaining requests
   */
  public checkLimit(
    category: string,
    key: string = 'default'
  ): { allowed: boolean; remaining: number; resetIn: number } {
    const config = this.configs[category] || DEFAULT_CONFIGS.api;
    const limitKey = `${category}:${key}`;
    const now = Date.now();

    let entry = this.limits.get(limitKey);

    // Reset if window expired
    if (!entry || now > entry.resetTime) {
      entry = {
        count: 0,
        resetTime: now + config.windowMs,
      };
    }

    const remaining = Math.max(0, config.maxRequests - entry.count);
    const resetIn = Math.max(0, entry.resetTime - now);

    if (entry.count >= config.maxRequests) {
      return { allowed: false, remaining: 0, resetIn };
    }

    // Consume one request
    entry.count++;
    this.limits.set(limitKey, entry);

    return {
      allowed: true,
      remaining: config.maxRequests - entry.count,
      resetIn,
    };
  }

  /**
   * Check if action would be allowed (without consuming)
   */
  public canProceed(category: string, key: string = 'default'): boolean {
    const config = this.configs[category] || DEFAULT_CONFIGS.api;
    const limitKey = `${category}:${key}`;
    const now = Date.now();

    const entry = this.limits.get(limitKey);

    if (!entry || now > entry.resetTime) {
      return true;
    }

    return entry.count < config.maxRequests;
  }

  /**
   * Reset limits for a specific category/key
   */
  public reset(category: string, key: string = 'default'): void {
    const limitKey = `${category}:${key}`;
    this.limits.delete(limitKey);
  }

  /**
   * Reset all limits
   */
  public resetAll(): void {
    this.limits.clear();
  }

  /**
   * Get current status for a category
   */
  public getStatus(
    category: string,
    key: string = 'default'
  ): { count: number; limit: number; resetIn: number } {
    const config = this.configs[category] || DEFAULT_CONFIGS.api;
    const limitKey = `${category}:${key}`;
    const now = Date.now();

    const entry = this.limits.get(limitKey);

    if (!entry || now > entry.resetTime) {
      return { count: 0, limit: config.maxRequests, resetIn: 0 };
    }

    return {
      count: entry.count,
      limit: config.maxRequests,
      resetIn: Math.max(0, entry.resetTime - now),
    };
  }

  /**
   * Add or update a rate limit config
   */
  public setConfig(category: string, config: RateLimitConfig): void {
    this.configs[category] = config;
  }
}

// Singleton instance
export const rateLimiter = RateLimiter.getInstance();

// Wrapper functions for common use cases

/**
 * Execute a function with rate limiting
 * @throws Error if rate limit exceeded
 */
export async function withRateLimit<T>(
  category: string,
  fn: () => Promise<T>,
  key: string = 'default'
): Promise<T> {
  const result = rateLimiter.checkLimit(category, key);

  if (!result.allowed) {
    const waitSeconds = Math.ceil(result.resetIn / 1000);
    throw new Error(
      `Rate limit exceeded for ${category}. Try again in ${waitSeconds} seconds.`
    );
  }

  return fn();
}

/**
 * Check sync rate limit (for data synchronization)
 */
export function canSync(userId: string = 'default'): boolean {
  return rateLimiter.canProceed('sync', userId);
}

/**
 * Consume sync rate limit
 */
export function consumeSync(userId: string = 'default'): boolean {
  return rateLimiter.checkLimit('sync', userId).allowed;
}

/**
 * Check login rate limit
 */
export function canLogin(identifier: string): boolean {
  return rateLimiter.canProceed('login', identifier);
}

/**
 * Consume login rate limit
 */
export function consumeLogin(identifier: string): { allowed: boolean; remaining: number } {
  const result = rateLimiter.checkLimit('login', identifier);
  return { allowed: result.allowed, remaining: result.remaining };
}

/**
 * Reset login rate limit (after successful login)
 */
export function resetLoginLimit(identifier: string): void {
  rateLimiter.reset('login', identifier);
}

export default rateLimiter;
